import { useEffect, useState } from 'react';
import { PRESET_TEMPLATES } from '@/data/presets';
import { fetchMasterProducts, type MasterGroup } from '@/sync/api';

/**
 * The product templates the add-product screen offers.
 *
 * From the back office, falling back to `PRESET_TEMPLATES` compiled into the
 * bundle. The fallback is not laziness: a shop setting itself up is standing in
 * the shop with a phone, and a blank picker there is a dead end. The bundled
 * list stays correct because a test in the back office parses `presets.ts` and
 * asserts the seeded catalogue still matches it.
 */
export type TemplateSource = 'server' | 'bundled';

export interface Templates {
  groups: MasterGroup[];
  source: TemplateSource;
  loading: boolean;
}

/** The bundled constant, widened to the shape the server sends. */
const BUNDLED: MasterGroup[] = PRESET_TEMPLATES.map((group) => ({
  cat: group.cat,
  // No masterId: these were never copied from a template, and claiming
  // provenance the shop does not have would be worse than admitting none.
  items: group.items as MasterGroup['items'],
}));

export function useTemplates(): Templates {
  const [state, setState] = useState<Templates>({
    groups: BUNDLED,
    source: 'bundled',
    loading: true,
  });

  useEffect(() => {
    let live = true;

    void (async () => {
      try {
        const groups = await fetchMasterProducts();
        if (live && groups.length) setState({ groups, source: 'server', loading: false });
        else if (live) setState((s) => ({ ...s, loading: false }));
      } catch {
        // Unreachable, or not enrolled. The bundled list is already showing and
        // is usable; saying so would be noise.
        if (live) setState((s) => ({ ...s, loading: false }));
      }
    })();

    return () => {
      live = false;
    };
  }, []);

  return state;
}
